import { useEffect, useMemo, useReducer, useRef } from 'react';
import { ApolloError, useQuery } from '@apollo/client';
import { Dayjs } from 'dayjs';

import { CRM_CALL_LOG } from './callLog.queries';
import { buildLogFilter, CallerCount, CallLogRow, CallLogState, CrmCallFilter, OutcomeCount } from './callLog';

interface CallLogData {
  crmCalls: {
    hasNextPage: boolean;
    endCursor: string | null;
    total?: number;
    byOutcome?: Array<OutcomeCount>;
    byCaller?: Array<CallerCount>;
    items: Array<CallLogRow>;
  };
}

export interface CallLogCounts {
  total: number;
  byOutcome: Array<OutcomeCount>;
  byCaller: Array<CallerCount>;
}

interface PageRequest {
  filter?: CrmCallFilter;
  take: number;
  after?: string;
  skip?: number;
  withCounts: boolean;
}

const codeOf = (error: ApolloError | undefined) => error?.graphQLErrors?.[0]?.extensions?.code;
const fieldOf = (error: ApolloError | undefined) => error?.graphQLErrors?.[0]?.extensions?.field;

/**
 * One page of the call log, asked the way the contact list asks (useContactPage):
 *
 * - **Next steps by cursor** (`after`). A page reached any other way (a link,
 *   a reload, the way back from a contact, "last page") is asked by `skip`.
 * - **The counts once per filter:** total, by outcome, by caller. Paging does
 *   not change them.
 * - **A page's request is fixed the first time it is asked,** so coming back
 *   to it is answered from Apollo's cache.
 */
export const useCallLogPage = (state: CallLogState, now: Dayjs) => {
  const filter = useMemo(() => buildLogFilter(state, now), [state, now]);
  const filterKey = JSON.stringify(filter ?? null);
  const listKey = JSON.stringify([filterKey, state.pageSize]);
  const requestKey = `${listKey}#${state.page}`;

  const memory = useRef({
    cursors: new Map<string, Map<number, string>>(),
    requests: new Map<string, PageRequest>(),
    counts: new Map<string, CallLogCounts>(),
  });
  const [, forget] = useReducer((n: number) => n + 1, 0);

  const cursorsFor = (key: string) => {
    let cursors = memory.current.cursors.get(key);
    if (!cursors) {
      cursors = new Map();
      memory.current.cursors.set(key, cursors);
    }
    return cursors;
  };

  let request = memory.current.requests.get(requestKey);
  if (!request) {
    const after = state.page > 0 ? cursorsFor(listKey).get(state.page) : undefined;
    request = {
      ...(filter ? { filter } : {}),
      take: state.pageSize,
      ...(after ? { after } : state.page > 0 ? { skip: state.page * state.pageSize } : {}),
      withCounts: !memory.current.counts.has(filterKey),
    };
    memory.current.requests.set(requestKey, request);
  }

  const { data, previousData, loading, error, refetch } = useQuery<CallLogData>(CRM_CALL_LOG, {
    variables: request,
    notifyOnNetworkStatusChange: true,
  });

  const page = data?.crmCalls;

  useEffect(() => {
    if (!page) return;
    if (page.total != null && page.byOutcome && page.byCaller) {
      memory.current.counts.set(filterKey, { total: page.total, byOutcome: page.byOutcome, byCaller: page.byCaller });
    }
    if (page.hasNextPage && page.endCursor) cursorsFor(listKey).set(state.page + 1, page.endCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // A cursor the API no longer accepts: drop it and ask by number instead.
  const staleCursor = Boolean(request.after) && codeOf(error) === 'CRM_INVALID_QUERY' && fieldOf(error) === 'after';
  useEffect(() => {
    if (!staleCursor) return;
    memory.current.requests.delete(requestKey);
    memory.current.cursors.delete(listKey);
    forget();
  }, [staleCursor, requestKey, listKey]);

  /** After a correction: everything remembered may be out of date. The cache is cleared by the caller. */
  const reset = () => {
    memory.current.requests.clear();
    memory.current.cursors.clear();
    memory.current.counts.clear();
    forget();
  };

  const shown = page ?? previousData?.crmCalls;
  const counts: CallLogCounts | undefined =
    page?.total != null && page.byOutcome && page.byCaller
      ? { total: page.total, byOutcome: page.byOutcome, byCaller: page.byCaller }
      : memory.current.counts.get(filterKey);

  return {
    filter,
    rows: shown?.items ?? [],
    loading,
    error: staleCursor ? undefined : error,
    hasNextPage: Boolean(page?.hasNextPage),
    counts,
    refetch,
    reset,
  };
};
